import type { SubscriptionBlock } from '@subscriptions/domain/entities';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';

/**
 * Maps a SubscriptionBlock domain entity to SubscriptionBlockViewDto for presentation.
 *
 * @param block - Domain entity
 * @returns View DTO for UI consumption
 */
export const mapSubscriptionBlockToViewDto = (block: SubscriptionBlock): SubscriptionBlockViewDto => ({
  uuid: block.uuid,
  name: block.name,
  extensionPos: block.extensionPos,
  extensionConnect: block.extensionConnect,
  total: block.total,
  paymentDate: block.paymentDate,
  interval: block.interval,
  status: block.status,
  subStatus: block.subStatus,
  customerState: block.customerState,
  inactiveSince: block.inactiveSince,
  advanceInvoice: block.advanceInvoice,
  groupInvoice: block.groupInvoice,
  configEnabled: block.configEnabled,
  paymentMethod: block.paymentMethod
    ? {
        uuid: block.paymentMethod.uuid,
        provider: block.paymentMethod.provider,
        providerId: block.paymentMethod.providerId,
        providerPublicId: block.paymentMethod.providerPublicId,
        methodType: block.paymentMethod.methodType,
        orgUuid: block.paymentMethod.orgUuid,
        brand: block.paymentMethod.brand,
        expirationDate: block.paymentMethod.expirationDate,
        lastFourDigits: block.paymentMethod.lastFourDigits,
        status: block.paymentMethod.status,
      }
    : null,
  invoiceData: block.invoiceData
    ? {
        uuid: block.invoiceData.uuid ?? '',
        orgUuid: block.invoiceData.orgUuid ?? '',
        businessName: block.invoiceData.businessName,
        firstName: block.invoiceData.firstName,
        firstLastName: block.invoiceData.firstLastName,
        secondLastName: block.invoiceData.secondLastName,
        postalCode: block.invoiceData.postalCode,
        rfc: block.invoiceData.rfc,
        cfdi: block.invoiceData.cfdi,
        email: block.invoiceData.email,
        taxRegime: block.invoiceData.taxRegime,
      }
    : null,
  lastError: block.lastError
    ? {
        code: block.lastError.code,
        concept: block.lastError.concept,
        date: block.lastError.date,
      }
    : null,
  discounts: block.discounts
    ? {
        total: block.discounts.total,
        expirationDate: block.discounts.expirationDate || null,
        stores: block.discounts.stores.map((store) => ({
          storeUuid: store.storeUuid,
          amount: store.amount,
          expirationDate: store.expirationDate || null,
        })),
      }
    : null,
  stores: [...block.stores].map((sp) => ({
    storeUuid: sp.storeUuid,
    storeName: sp.storeName,
    plan: sp.plan,
    churnedAt: sp.churnedAt,
    concepts: sp.concepts.map((c) => ({ ...c })),
  })),
});
